/* Canvello — product page: Make an offer, Ask a question, Share, and the
 * trade price / incoming date per variant (Task 7.5; offer rebuilt 2026-10-17).
 *
 * The dialog (snippets/cv-talk.liquid) is a native <dialog> around Shopify's
 * contact form. This script:
 *   - opens it in a mode (offer | ask) from [data-cv-talk-open], and on load
 *     when the page comes back with the form posted or refused;
 *   - answers an offer as it is typed, in plain words: accepted at that price
 *     (at or above "Offers accepted from", data-floor %), or "we'll offer you
 *     X" under it, or "that's the full price"; three one-tap offers (−5, −10,
 *     −15 %) and a quantity stepper;
 *   - with the Canvello API (data-api), sends the offer there and shows the
 *     answer on the spot: an accepted offer's single-use code and checkout
 *     link, or the counter-offer with one button to take it. The answer is
 *     kept per variant (localStorage "cv:offers2") and shown again on the
 *     next visit while it is valid. The store also gets the usual email,
 *     with the answer in it. If the API can't be reached the form is sent
 *     by email as before. Without the API the form simply posts;
 *   - follows the chosen variant (cv:variant from cv-product.js): photo,
 *     options, price, SKU and link in the dialog, the share links, the trade
 *     price and the incoming date on the page.
 * Without JS the dialog is never shown and the tools do nothing; the
 * product page itself is untouched.
 */
(() => {
  const OFFERS = 'cv:offers2';
  const readOffers = () => {
    try {
      const o = JSON.parse(localStorage.getItem(OFFERS) || '{}');
      return o && typeof o === 'object' ? o : {};
    } catch (e) {
      return {};
    }
  };
  const keepOffer = (vid, answer) => {
    try {
      const o = readOffers();
      if (answer) o[vid] = Object.assign({ at: Date.now() }, answer);
      else delete o[vid];
      localStorage.setItem(OFFERS, JSON.stringify(o));
    } catch (e) {
      /* storage blocked: the answer is shown once */
    }
  };
  const fill = (s, map) => String(s || '').replace(/\[(\w+)\]/g, (m, k) => (k in map ? map[k] : m));

  const parseMoney = (raw) => {
    let s = String(raw || '').replace(/[^\d.,]/g, '');
    if (!s) return NaN;
    // "1.234,50" / "1,234.50" / "120,5" / "120.5": the last separator with
    // one or two digits after it is the decimal one.
    const m = s.match(/^(.*?)[.,](\d{1,2})$/);
    if (m) s = m[1].replace(/[.,]/g, '') + '.' + m[2];
    else s = s.replace(/[.,]/g, '');
    return Math.round(parseFloat(s) * 100);
  };

  function setupTalk(dlg) {
    const product = dlg.closest('cv-product') || document;
    const form = dlg.querySelector('[data-cv-talk-form]');
    const offer = dlg.querySelector('[data-cv-talk-offer]');
    const qty = dlg.querySelector('[data-cv-talk-qty]');
    const hint = dlg.querySelector('[data-cv-talk-hint]');
    const result = dlg.querySelector('[data-cv-talk-result]');
    const send = dlg.querySelector('[data-cv-talk-send]');
    const sendLabel = dlg.querySelector('[data-cv-talk-send-label]');
    const api = (dlg.dataset.api || '').replace(/\/+$/, '');
    const root = dlg.dataset.root && dlg.dataset.root !== '/' ? `${dlg.dataset.root}/` : '/';
    const level = (Number(dlg.dataset.floor) || 85) / 100;
    const t = dlg.dataset;
    let cents = Number(dlg.dataset.cents) || 0;
    let vid = dlg.dataset.vid;
    let mode = 'offer';
    let opener = null;
    let busy = false;

    const nf = (() => {
      try {
        return new Intl.NumberFormat(dlg.dataset.locale || undefined, { style: 'currency', currency: dlg.dataset.currency || 'USD' });
      } catch (e) {
        return { format: (n) => n.toFixed(2) };
      }
    })();
    const money = (c) => nf.format(c / 100).replace(/[.,]00(?=\D*$)/, '');
    const plain = (c) => (c / 100).toFixed(2).replace(/\.00$/, '');
    // To the nearest cent, as the offer service decides: exactly 85 % is in.
    const lowest = () => Math.round(cents * level);
    const pieces = () => Math.max(1, Math.min(99, parseInt(qty && qty.value, 10) || 1));

    // Where the offer stands. Owner's rule (2026-10-20): the visitor never
    // sees it before sending (no "we accept" / "we'd counter"); only the
    // store's email and the answer after sending carry it.
    const check = () => {
      if (!offer) return 'idle';
      offer.setCustomValidity('');
      const c = parseMoney(offer.value);
      const n = pieces();
      let state = 'idle';
      let msg = t.tIdle;
      if (offer.value.trim() && !isNaN(c) && c > 0) {
        if (c >= cents) {
          state = 'full';
          msg = t.tFull;
          offer.setCustomValidity(t.tFull);
        } else {
          state = c >= lowest() ? 'yes' : 'counter';
        }
      }
      dlg.dataset.state = state === 'full' ? 'full' : 'idle';
      if (hint) hint.textContent = msg;
      dlg.querySelectorAll('[data-cv-talk-pct]').forEach((b) => {
        const want = Math.round((cents * (100 - Number(b.dataset.cvTalkPct))) / 100);
        b.setAttribute('aria-pressed', String(!isNaN(c) && c === want));
      });
      const pct = dlg.querySelector('[data-cv-talk-f="pct"]');
      if (pct) pct.value = state === 'idle' ? '' : `${Math.round((c / cents) * 100)}% of ${money(cents)} × ${n}`;
      const answer = dlg.querySelector('[data-cv-talk-f="answer"]');
      if (answer) answer.value = state === 'yes' ? 'Within the accepted range' : state === 'counter' ? `Under the accepted range: counter at ${money(lowest())}` : '';
      return state;
    };

    const quickPrices = () => {
      dlg.querySelectorAll('[data-cv-talk-pct]').forEach((b) => {
        const el = b.querySelector('[data-cv-talk-pct-price]');
        if (el) el.textContent = money(Math.round((cents * (100 - Number(b.dataset.cvTalkPct))) / 100));
      });
    };

    const setMode = (m) => {
      mode = m === 'ask' || !offer ? 'ask' : 'offer';
      dlg.dataset.mode = mode;
      const panel = dlg.querySelector('[data-cv-talk-panel="offer"]');
      if (panel) panel.hidden = mode !== 'offer';
      const radio = dlg.querySelector(`[data-cv-talk-mode="${mode}"]`);
      if (radio) radio.checked = true;
      const title = dlg.querySelector('[data-cv-talk-title]');
      if (title) title.textContent = mode === 'offer' ? t.tOfferTitle : t.tAskTitle;
      // The question is its own field in ask mode; for offers a note folds away.
      const q = dlg.querySelector('[data-cv-talk-q]');
      const body = dlg.querySelector('[data-cv-talk-body]');
      const note = dlg.querySelector('[data-cv-talk-note-in]');
      const noteFld = dlg.querySelector('[data-cv-talk-note-fld]');
      if (q) q.hidden = mode !== 'ask';
      if (body) {
        body.disabled = mode !== 'ask';
        body.required = mode === 'ask';
      }
      if (note) note.disabled = mode !== 'offer';
      if (noteFld) noteFld.hidden = mode !== 'offer';
      if (offer) {
        offer.disabled = mode !== 'offer';
        offer.required = mode === 'offer';
      }
      if (qty) qty.disabled = mode !== 'offer';
      const terms = dlg.querySelector('[data-cv-talk-terms]');
      if (terms) terms.textContent = terms.dataset[mode === 'offer' ? 'nOffer' : 'nAsk'];
      if (sendLabel && send) sendLabel.textContent = send.dataset[mode === 'offer' ? 'sOffer' : 'sAsk'];
      check();
    };

    // ---- The answer screen ---------------------------------------------
    const showForm = () => {
      if (result) result.hidden = true;
      if (form) form.hidden = false;
    };
    const button = (label, cls, onClick, href) => {
      const el = document.createElement(href ? 'a' : 'button');
      if (href) el.href = href;
      else el.type = 'button';
      el.className = cls;
      el.textContent = label;
      if (onClick) el.addEventListener('click', onClick);
      return el;
    };
    const showAnswer = (a) => {
      if (!result) return;
      const $ = (k) => result.querySelector(`[data-cv-talk-r-${k}]`);
      const actions = $('actions');
      actions.textContent = '';
      $('code').hidden = true;
      result.dataset.status = a.status;
      const n = a.qty || 1;
      if (a.status === 'accepted') {
        $('mark').textContent = '✓';
        $('title').textContent = t.tAcceptedTitle;
        $('text').textContent = fill(t.tAcceptedText, { price: money(a.price), qty: String(n), total: money(a.price * n) });
        $('code').hidden = !a.code;
        $('code-text').textContent = a.code || '';
        let day = '';
        if (a.ends) {
          try {
            day = new Date(a.ends).toLocaleString(dlg.dataset.locale || undefined, { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' });
          } catch (e) {
            day = new Date(a.ends).toLocaleString();
          }
        }
        $('valid').textContent = day ? fill(t.tValid, { date: day }) : '';
        if (a.url) actions.append(button(t.tCheckout, 'cv-btn cv-talk__btn', null, a.url));
        if (a.code) actions.append(button(t.tAdd, 'cv-btn cv-btn--ghost cv-talk__btn2', () => addToBag(a)));
      } else if (a.status === 'counter') {
        $('mark').textContent = '↺';
        $('title').textContent = fill(t.tCounterTitle, { price: money(a.price) });
        $('text').textContent = fill(t.tCounterText, { offer: money(a.offer || 0), price: money(a.price) });
        actions.append(button(fill(t.tAccept, { price: money(a.price) }), 'cv-btn cv-talk__btn', () => acceptCounter(a)));
        actions.append(button(t.tDecline, 'cv-talk__link', () => {
          keepOffer(vid, { status: 'declined', price: a.price, qty: n });
          showAnswer({ status: 'declined', qty: n });
        }));
      } else if (a.status === 'declined' || a.status === 'used') {
        $('mark').textContent = '·';
        $('title').textContent = a.status === 'used' ? t.tUsed : t.tDeclined;
        $('text').textContent = '';
        actions.append(button(t.tAgain, 'cv-talk__link', () => {
          keepOffer(vid, null);
          showForm();
          if (offer) offer.focus();
        }));
      }
      if (form) form.hidden = true;
      result.hidden = false;
      requestAnimationFrame(() => result.focus({ preventScroll: true }));
    };

    const post = (path, body) =>
      fetch(`${api}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.assign({ sig: dlg.dataset.sig || '' }, body)),
      }).then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))));

    const setBusy = (on) => {
      busy = on;
      if (send) {
        send.disabled = on;
        send.setAttribute('aria-busy', String(on));
      }
      if (sendLabel && on) sendLabel.textContent = t.tSending;
      if (sendLabel && !on && send) sendLabel.textContent = send.dataset[mode === 'offer' ? 'sOffer' : 'sAsk'];
    };

    // A copy of every offer reaches the store by email (the contact form),
    // with what the API answered.
    const mailCopy = (answerText) => {
      if (!form) return;
      const answer = dlg.querySelector('[data-cv-talk-f="answer"]');
      if (answer) answer.value = answerText;
      try {
        fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin' }).catch(() => {});
      } catch (e) {
        /* the API's own record stands */
      }
    };

    const acceptCounter = (a) => {
      if (busy) return;
      setBusy(true);
      post('/offer/accept', { token: a.token })
        .then((r) => {
          if (r.status === 'accepted') {
            keepOffer(vid, r);
            showAnswer(r);
          } else showAnswer(r);
        })
        .catch(() => {
          // The service is down: the studio answers by email instead.
          showForm();
          if (form) form.submit();
        })
        .then(() => setBusy(false));
    };

    const addToBag = (a) => {
      const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
      fetch(`${root}cart/add.js`, { method: 'POST', headers: json, body: JSON.stringify({ items: [{ id: Number(a.variant || vid), quantity: a.qty || 1 }] }) })
        .then(() => fetch(`${root}cart.js`).then((r) => r.json()))
        .then((cart) => {
          const codes = (cart.discount_codes || []).map((d) => d.code).filter((c) => c.toLowerCase() !== a.code.toLowerCase());
          codes.push(a.code);
          return fetch(`${root}cart/update.js`, { method: 'POST', headers: json, body: JSON.stringify({ discount: codes.join(',') }) });
        })
        .then(() => {
          dlg.close();
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cv-talk', open: true } }));
        })
        .catch(() => {
          if (a.url) location.href = a.url;
        });
    };

    // ---- Open, close ----------------------------------------------------
    // The dialog's styles load after the page (cv-product.liquid, media=print
    // then all). A click that beats them waits (4s at most) instead of
    // opening a bare dialog.
    const styled = () =>
      Promise.all(
        [...document.querySelectorAll('link[href*="cv-talk.css"], link[href*="cv-form.css"]')].map((l) => {
          l.media = 'all';
          if (l.sheet) return null;
          return new Promise((ok) => {
            l.addEventListener('load', ok, { once: true });
            l.addEventListener('error', ok, { once: true });
            setTimeout(ok, 4000);
          });
        })
      );

    const open = (m, from) => styled().then(() => show(m, from));
    const show = (m, from) => {
      if (dlg.open) return;
      opener = from || document.activeElement;
      if (m) setMode(m);
      const kept = mode === 'offer' && readOffers()[vid];
      if (kept && (!kept.ends || new Date(kept.ends) > new Date())) showAnswer(kept);
      else showForm();
      if (typeof dlg.showModal === 'function') dlg.showModal();
      else dlg.setAttribute('open', '');
      document.documentElement.setAttribute('data-cv-locked', '');
      if (!result || result.hidden) {
        const first = dlg.querySelector(mode === 'offer' && offer ? '[data-cv-talk-offer]' : '[data-cv-talk-posted], .cv-pg-msg, [data-cv-talk-body]');
        // Phones: no keyboard over the answer before the visitor taps a field.
        const touch = matchMedia('(hover: none)').matches;
        requestAnimationFrame(() => first && (!touch || first.tagName !== 'INPUT') && first.focus({ preventScroll: true }));
      }
    };
    const close = () => (dlg.open ? dlg.close() : null);

    dlg.addEventListener('close', () => {
      document.documentElement.removeAttribute('data-cv-locked');
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    });
    dlg.addEventListener('click', (e) => {
      if (e.target.closest('[data-cv-talk-close]')) return close();
      const pct = e.target.closest('[data-cv-talk-pct]');
      if (pct && offer) {
        offer.value = plain(Math.round((cents * (100 - Number(pct.dataset.cvTalkPct))) / 100));
        check();
        return;
      }
      const step = e.target.closest('[data-cv-talk-step]');
      if (step && qty) {
        qty.value = String(Math.max(1, Math.min(99, pieces() + Number(step.dataset.cvTalkStep))));
        check();
        return;
      }
      if (e.target.closest('[data-cv-talk-change]')) {
        const f = dlg.querySelector('[data-cv-talk-fields]');
        const who = dlg.querySelector('[data-cv-talk-who]');
        if (f) f.hidden = false;
        if (who) who.hidden = true;
        const name = f && f.querySelector('input');
        if (name) name.focus();
        return;
      }
      const copy = e.target.closest('[data-cv-talk-copy]');
      if (copy) {
        const code = result.querySelector('[data-cv-talk-r-code-text]').textContent;
        if (navigator.clipboard) {
          navigator.clipboard.writeText(code).then(() => {
            copy.textContent = t.tCopied;
            setTimeout(() => (copy.textContent = t.tCopy), 2200);
          }, () => {});
        }
        return;
      }
      if (e.target === dlg) {
        const r = dlg.getBoundingClientRect();
        const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
        if (!inside) close();
      }
    });
    dlg.addEventListener('change', (e) => {
      if (e.target.matches('[data-cv-talk-mode]')) setMode(e.target.dataset.cvTalkMode);
      if (e.target === qty) check();
    });
    if (offer) offer.addEventListener('input', check);
    if (qty) qty.addEventListener('input', check);

    if (form) {
      form.addEventListener('submit', (e) => {
        const state = check();
        if (!form.checkValidity()) {
          e.preventDefault();
          form.reportValidity();
          return;
        }
        if (mode !== 'offer' || !api) return; // the email form posts as before
        e.preventDefault();
        if (busy) return;
        const c = parseMoney(offer.value);
        const fd = new FormData(form);
        setBusy(true);
        post('/offer', {
          variant: vid,
          qty: pieces(),
          offer: c,
          price: cents,
          currency: dlg.dataset.currency,
          handle: dlg.dataset.handle,
          email: fd.get('contact[email]') || '',
          name: fd.get('contact[name]') || '',
          phone: fd.get('contact[phone]') || '',
          note: fd.get('contact[body]') || '',
          locale: dlg.dataset.locale,
          country: dlg.dataset.country,
        })
          .then((a) => {
            a.offer = c;
            a.qty = a.qty || pieces();
            if (a.status === 'accepted' || a.status === 'counter' || a.status === 'used') {
              keepOffer(vid, a);
              showAnswer(a);
              mailCopy(
                a.status === 'accepted'
                  ? `Accepted automatically at ${money(a.price)} × ${a.qty}; code ${a.code}`
                  : a.status === 'counter'
                    ? `Countered automatically at ${money(a.price)} × ${a.qty}`
                    : 'Repeat offer: the earlier answer was shown again'
              );
            } else if (a.status === 'full') {
              if (hint) hint.textContent = t.tFull;
            } else throw new Error('answer');
          })
          .catch(() => {
            // The service can't be reached: send it the old way, by email.
            setBusy(false);
            if (state !== 'full') HTMLFormElement.prototype.submit.call(form);
          })
          .then(() => setBusy(false));
      });
    }

    document.addEventListener('click', (e) => {
      const btn = e.target.closest && e.target.closest('[data-cv-talk-open]');
      if (!btn || btn.getAttribute('aria-controls') !== dlg.id) return;
      e.preventDefault();
      open(btn.dataset.cvTalkOpen, btn);
    });

    // The variant follows the page.
    product.addEventListener('cv:variant', (e) => {
      const v = e.detail && e.detail.variant;
      if (!v) return;
      cents = Number(v.cents) || cents;
      vid = String(v.id || vid);
      dlg.dataset.cents = String(cents);
      dlg.dataset.vid = vid;
      const set = (k, val) => {
        const el = dlg.querySelector(`[data-cv-talk-f="${k}"]`);
        if (el) el.value = val;
      };
      set('variant', v.title);
      set('sku', v.sku || '');
      set('id', v.id);
      set('list', `${v.price} ${dlg.dataset.currency || ''}`.trim());
      const link = dlg.querySelector('[data-cv-talk-f="link"]');
      if (link) link.value = link.value.replace(/variant=\d+/, `variant=${v.id}`);
      const price = dlg.querySelector('[data-cv-talk-price]');
      if (price) price.textContent = v.price;
      const vt = dlg.querySelector('[data-cv-talk-variant]');
      if (vt && vt.textContent.trim()) vt.textContent = v.label || v.title;
      quickPrices();
      check();
    });

    setMode(offer ? 'offer' : 'ask');
    quickPrices();
    // Back from sending (or a refusal): show the dialog's answer.
    if (dlg.querySelector('[data-cv-talk-posted], [data-cv-talk-error]')) {
      const posted = dlg.querySelector('[data-cv-talk-posted]');
      requestAnimationFrame(() => open(null, null));
      if (posted) {
        const url = new URL(location.href);
        url.searchParams.delete('contact_posted');
        history.replaceState(history.state, '', url.pathname + url.search);
      }
    }
  }

  function setupShare(box) {
    const panel = box.querySelector('[popover]');
    const live = box.querySelector('[data-cv-share-live]');
    const url = () => box.dataset.url;
    box.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-cv-share-open]');
      // Phones: the system sheet instead of the panel.
      if (opener && navigator.share && matchMedia('(hover: none)').matches) {
        e.preventDefault();
        if (panel && panel.hidePopover) {
          try {
            panel.hidePopover();
          } catch (err) {
            /* not open */
          }
        }
        navigator.share({ title: box.dataset.title, url: url() }).catch(() => {});
        return;
      }
      const copy = e.target.closest('[data-cv-share-copy]');
      if (copy) {
        const done = () => {
          const t = copy.textContent;
          copy.textContent = copy.dataset.done;
          if (live) live.textContent = copy.dataset.done;
          setTimeout(() => (copy.textContent = t), 2200);
        };
        if (navigator.clipboard) navigator.clipboard.writeText(url()).then(done, () => {});
      }
    });
    // Without popover support the panel stays a hidden list of links.
    if (!(panel && 'popover' in HTMLElement.prototype)) {
      const opener = box.querySelector('[data-cv-share-open]');
      if (opener && panel) {
        panel.hidden = true;
        opener.addEventListener('click', () => (panel.hidden = !panel.hidden));
      }
    }
    const product = box.closest('cv-product');
    if (product) {
      product.addEventListener('cv:variant', (e) => {
        const v = e.detail && e.detail.variant;
        if (!v || e.detail.first) return;
        const base = box.dataset.url.split('?')[0];
        const next = `${base}?variant=${v.id}`;
        const enc = encodeURIComponent;
        box.querySelectorAll('a[data-cv-share-net]').forEach((a) => {
          a.href = a.href.split(enc(box.dataset.url)).join(enc(next));
        });
        box.dataset.url = next;
      });
    }
  }

  // The trade price and the incoming date on the page follow the variant.
  function setupPage(product) {
    product.addEventListener('cv:variant', (e) => {
      const v = e.detail && e.detail.variant;
      if (v && v.trade) product.querySelectorAll('[data-cv-pdp-trade]').forEach((el) => (el.textContent = v.trade));
      const inc = product.querySelector('[data-cv-pdp-incoming]');
      if (inc) {
        inc.hidden = !(v && v.incoming);
        if (v && v.incoming) inc.textContent = fill(inc.dataset.t, { date: v.incoming });
      }
    });
  }

  const start = () => {
    document.querySelectorAll('dialog[data-cv-talk]').forEach(setupTalk);
    document.querySelectorAll('[data-cv-share]').forEach(setupShare);
    document.querySelectorAll('cv-product').forEach(setupPage);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
